import { ICONS, SortableWidgetProps, Tooltip, Widget } from '@creditsights/styleguide-web';
import React, { forwardRef, ReactElement } from 'react';
import { RiskOverviewMetrics } from '../../../models/RiskProducts';
import { convertToPercentage } from '../../../utils/riskProducts.utils';
import ScoreCharts from '../../ScoreCharts';
import CircleBar from '../../ScoreCharts/CircleBar';
import ScoreLine from '../../ScoreCharts/ScoreLine';
import { creColorConfig, CreditRiskLevel } from './CreSummary.constants';
import { formatCreCircleBarLabel, formatPeriod, formatStepValue, Trend } from './RiskSummary.logic';

export interface CreSummaryWidgetProps extends SortableWidgetProps {
  riskOverviewMetrics?: RiskOverviewMetrics;
  isError: boolean;
  onErrorRetry: () => void;
}

const CreSummaryWidget = (
  { riskOverviewMetrics, isError, onErrorRetry, ...sortableWidgetProps }: CreSummaryWidgetProps,
  ref
): ReactElement | null => {
  const {
    canAccess,
    cre_1y: cre,
    cre_trend_1y: trend1year,
    cre_trend_6m: trend6months,
  } = riskOverviewMetrics?.cre ?? { canAccess: true, cre_1y: null, cre_trend_1y: null, cre_trend_6m: null };
  const { credit_risk_level: crl } = riskOverviewMetrics?.cqs ?? { credit_risk_level: null };
  const crePercentage = cre !== null ? convertToPercentage(cre, 2) : null;
  const creTooltip = cre !== null ? convertToPercentage(cre) : null;
  const period = formatPeriod(crePercentage);

  return (
    <Widget
      headerTitle={
        <Tooltip content="The Credit Risk Estimate (CRE) is a one-year probability of default. The Credit Risk Level (CRL) indicates a company’s short-term default risk relative to its peers and historical averages.">
          <div>Credit Risk Estimate</div>
        </Tooltip>
      }
      classNames="score-chart-widget"
      headerIcon={!canAccess ? { icon: ICONS.LockOutline, dataTestId: 'cre-lock-icon' } : undefined}
      dataTestId="cre-summary-widget"
      isError={isError}
      fixedHeight
      onRetryAfterErrorClick={onErrorRetry}
      ref={ref}
      {...sortableWidgetProps}
    >
      <ScoreCharts
        leftSide={
          <Tooltip content={creTooltip} disabled={!creTooltip}>
            <div>
              <CircleBar
                value={crePercentage || 0}
                color={creColorConfig[period]}
                label={formatCreCircleBarLabel(crePercentage, canAccess)}
              />
            </div>
          </Tooltip>
        }
        rightSide={
          <>
            <ScoreLine
              title="Trend 6 Mo"
              steps={Trend}
              activeStep={formatStepValue(trend6months, Trend, canAccess)}
              tooltip="Overall change in a company's Credit Risk Level (CRL) over the past six months."
              dataTestId="trend-6-score-line"
            />
            <ScoreLine
              title="Trend 12 Mo"
              steps={Trend}
              activeStep={formatStepValue(trend1year, Trend, canAccess)}
              tooltip="Overall change in a company's Credit Risk Level (CRL) over the past year."
              dataTestId="trend-12-score-line"
            />
            <ScoreLine
              title="Credit Risk Level"
              steps={CreditRiskLevel}
              activeStep={formatStepValue(crl, CreditRiskLevel, canAccess)}
              tooltip="Level of a company's CRE relative to other companies within the same CRE model."
              dataTestId="cre-score-line"
            />
          </>
        }
      />
    </Widget>
  );
};

export default forwardRef(CreSummaryWidget);
